/**
 * Lee el horario tal y como viene de OpenStreetMap.
 *
 * El formato es "Mo-Fr 09:00-22:00; Sa,Su 10:00-23:30" y admite mil variantes.
 * Aqui se interpretan las que cubren la practica totalidad de los bares y
 * restaurantes; lo que no se entienda se ensena tal cual en vez de inventarselo,
 * que un horario mal puesto es peor que no ponerlo.
 */

const DIAS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;

/**
 * Los dias en castellano. Las paginas del directorio usan los de su idioma
 * (lib/directorio/idiomas.ts); esto se queda como reserva para quien llame sin
 * decir en que idioma quiere el horario.
 */
export const NOMBRE_DIA = [
  "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo",
];

export type TramoDelDia = { desde: number; hasta: number };

/** Los tramos de cada dia de la semana, de lunes (0) a domingo (6). */
export type Semana = TramoDelDia[][];

function minutos(hhmm: string): number | null {
  const partes = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!partes) return null;
  const h = Number(partes[1]);
  const m = Number(partes[2]);
  if (h > 48 || m > 59) return null;
  return h * 60 + m;
}

function indicesDeDias(expresion: string): number[] {
  const indices = new Set<number>();
  for (const trozo of expresion.split(",")) {
    const rango = trozo.trim().match(/^([A-Za-z]{2})\s*-\s*([A-Za-z]{2})$/);
    if (rango) {
      const desde = DIAS.indexOf(rango[1] as any);
      const hasta = DIAS.indexOf(rango[2] as any);
      if (desde < 0 || hasta < 0) continue;
      // "Sa-Mo" cruza el final de la semana.
      for (let i = desde; ; i = (i + 1) % 7) {
        indices.add(i);
        if (i === hasta) break;
      }
      continue;
    }
    const suelto = DIAS.indexOf(trozo.trim() as any);
    if (suelto >= 0) indices.add(suelto);
  }
  return [...indices];
}

/** Convierte el texto de OSM en una semana, o devuelve null si no se entiende. */
export function leerHorario(texto: string | null | undefined): Semana | null {
  if (!texto) return null;
  const limpio = texto.trim();
  if (!limpio || /^(24\/7)$/i.test(limpio)) {
    if (/^24\/7$/i.test(limpio)) {
      return Array.from({ length: 7 }, () => [{ desde: 0, hasta: 24 * 60 }]);
    }
    return null;
  }

  const semana: Semana = Array.from({ length: 7 }, () => []);
  let algoEntendido = false;

  for (const regla of limpio.split(";")) {
    const trozo = regla.trim();
    if (!trozo || /off|closed/i.test(trozo)) continue;

    const partes = trozo.match(/^([A-Za-z]{2}(?:\s*[-,]\s*[A-Za-z]{2})*)\s+(.+)$/);
    // Sin dias delante, la regla vale para toda la semana ("09:00-22:00").
    const dias = partes ? indicesDeDias(partes[1]) : [0, 1, 2, 3, 4, 5, 6];
    const horas = partes ? partes[2] : trozo;
    if (dias.length === 0) continue;

    for (const tramo of horas.split(",")) {
      const rango = tramo.trim().match(/^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
      if (!rango) continue;
      const desde = minutos(rango[1]);
      let hasta = minutos(rango[2]);
      if (desde == null || hasta == null) continue;
      // "20:00-02:00": cierra de madrugada, muy comun en bares.
      if (hasta <= desde) hasta += 24 * 60;
      for (const dia of dias) semana[dia].push({ desde, hasta });
      algoEntendido = true;
    }
  }

  return algoEntendido ? semana : null;
}

function formatoHora(minutosTotales: number): string {
  const normalizado = minutosTotales % (24 * 60);
  const h = Math.floor(normalizado / 60);
  const m = normalizado % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function textoDelDia(
  tramos: TramoDelDia[],
  etiquetas: { cerrado: string; abierto24h: string } = {
    cerrado: "Cerrado",
    abierto24h: "Abierto 24 h",
  },
): string {
  if (tramos.length === 0) return etiquetas.cerrado;
  if (tramos.some((t) => t.desde === 0 && t.hasta >= 24 * 60)) return etiquetas.abierto24h;
  return tramos
    .slice()
    .sort((a, b) => a.desde - b.desde)
    .map((t) => `${formatoHora(t.desde)}–${formatoHora(t.hasta)}`)
    .join(" y ");
}

/**
 * La zona horaria del directorio.
 *
 * Un negocio abre a SU hora, no a la de quien lo mira. Sin esto, un britanico
 * consultando desde Londres ve "cerrado" un sitio que esta abierto, y a un ruso
 * le bailan dos horas: justo el publico para el que existe el directorio en
 * nueve idiomas. Cuando el directorio salga de Baleares, esto tendra que venir
 * del lugar y no de una constante.
 */
export const ZONA = "Europe/Madrid";

/** El dia de la semana (0 = lunes) y los minutos, en hora del negocio. */
export function momentoEnZona(cuando: Date, zona = ZONA): { dia: number; minutos: number } {
  const partes = new Intl.DateTimeFormat("en-GB", {
    timeZone: zona,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(cuando);

  const trozo = (tipo: string) => partes.find((p) => p.type === tipo)?.value || "";
  const dias: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const hora = Number(trozo("hour"));
  return {
    dia: dias[trozo("weekday")] ?? 0,
    // A medianoche algunos entornos devuelven 24 en vez de 0.
    minutos: (hora % 24) * 60 + Number(trozo("minute")),
  };
}

/**
 * Si esta abierto en un instante dado.
 *
 * Se le pasa la fecha desde fuera para que la pagina pueda decidir si lo calcula
 * en el servidor o en el navegador: una pagina cacheada una hora no puede decir
 * "abierto ahora" con la hora del build. La ZONA, en cambio, no se negocia: es
 * la del negocio.
 */
export function estaAbierto(semana: Semana, cuando: Date): boolean {
  const { dia, minutos } = momentoEnZona(cuando);

  if (semana[dia].some((t) => minutos >= t.desde && minutos < t.hasta)) return true;
  // Un tramo de ayer que cruza la medianoche sigue contando hoy.
  const ayer = (dia + 6) % 7;
  return semana[ayer].some((t) => t.hasta > 24 * 60 && minutos < t.hasta - 24 * 60);
}

/** El horario en formato schema.org, para el dato estructurado de la ficha. */
export function horarioSchema(semana: Semana): string[] {
  const abreviatura = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  const salida: string[] = [];
  semana.forEach((tramos, dia) => {
    for (const tramo of tramos) {
      salida.push(`${abreviatura[dia]} ${formatoHora(tramo.desde)}-${formatoHora(tramo.hasta)}`);
    }
  });
  return salida;
}

function horaDeCierre(minutosTotales: number): string {
  // "24:00" y no "00:00": "00:00-00:00" no lo entiende nadie como abierto todo el dia.
  return minutosTotales === 24 * 60 ? "24:00" : formatoHora(minutosTotales);
}

/**
 * Lo contrario de leerHorario: la semana, al texto que se guarda.
 *
 * Es lo que escribe el editor de horario del panel, asi que el dueño elige dia
 * a dia y nunca ve este formato. Junta los dias seguidos con el mismo horario
 * ("Mo-Fr 12:00-16:00,19:00-23:30; Sa 12:00-23:00") y los cerrados no se
 * escriben: un dia que no sale, esta cerrado. Devuelve null si no abre nunca.
 */
export function escribirHorario(semana: Semana): string | null {
  const deCadaDia = semana.map((tramos) =>
    tramos
      .slice()
      .sort((a, b) => a.desde - b.desde)
      .map((t) => `${formatoHora(t.desde)}-${horaDeCierre(t.hasta)}`)
      .join(","),
  );
  if (deCadaDia.every((d) => d === "")) return null;
  if (deCadaDia.every((d) => d === "00:00-24:00")) return "24/7";

  const reglas: string[] = [];
  let inicio = 0;
  for (let dia = 1; dia <= 7; dia++) {
    if (dia < 7 && deCadaDia[dia] === deCadaDia[inicio]) continue;
    if (deCadaDia[inicio]) {
      const fin = dia - 1;
      const dias = fin === inicio ? DIAS[inicio] : `${DIAS[inicio]}-${DIAS[fin]}`;
      reglas.push(`${dias} ${deCadaDia[inicio]}`);
    }
    inicio = dia;
  }
  return reglas.join("; ");
}
